import type { Comment, DeleteCommentResult, Thread } from '@atlas/api-contract';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { toDisplayableError } from '@/components/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  describeCommentDeletion,
  isFirstComment,
  threadReplyCount,
} from './thread-display';
import { useDeleteComment } from './threads-queries';

export interface DeleteCommentDialogProps {
  /** The comment queued for deletion, or `null` when none is. */
  readonly comment: Comment | null;
  /** The thread it belongs to, for working out what deleting it destroys. */
  readonly thread: Thread;
  readonly versionId: string;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Called with the server's answer once the delete has succeeded.
   *
   * The popover needs it: `threadDeleted` decides whether it closes because the
   * thread has gone, or simply re-renders one reply lighter.
   */
  readonly onDeleted: (result: DeleteCommentResult) => void;
}

/**
 * The confirmation in front of every comment delete (spec R5.5).
 *
 * A plain dialog, not the typed-name confirmation the prototype delete uses:
 * the blast radius is one conversation rather than every version of a
 * prototype, and making the common case that hard trains people to type through
 * confirmations without reading them.
 *
 * What it *does* insist on is honesty about the blast radius that does exist.
 * Deleting the comment that opened a thread deletes the thread, its pin, and
 * every reply under it — the description says so, and counts them, because the
 * replies belong to other people and the person clicking delete cannot see them
 * all in the popover behind the dialog.
 */
export const DeleteCommentDialog = ({
  comment,
  thread,
  versionId,
  onOpenChange,
  onDeleted,
}: DeleteCommentDialogProps) => {
  const deleteComment = useDeleteComment();
  const [error, setError] = useState<string | null>(null);

  const open = comment !== null;

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const first = comment !== null && isFirstComment(thread, comment.id);
  const replies = threadReplyCount(thread);

  const handleDelete = async (): Promise<void> => {
    if (comment === null) {
      return;
    }

    setError(null);

    try {
      const result = await deleteComment.mutateAsync({
        versionId,
        commentId: comment.id,
      });

      onDeleted(result);
      onOpenChange(false);
    } catch (caught: unknown) {
      setError(toDisplayableError(caught));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && deleteComment.isPending) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {first ? 'Delete this thread?' : 'Delete this reply?'}
          </DialogTitle>
          <DialogDescription>
            {describeCommentDeletion(first, replies)}
          </DialogDescription>
        </DialogHeader>

        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={deleteComment.isPending}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteComment.isPending}
            onClick={() => {
              void handleDelete();
            }}
          >
            {deleteComment.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="animate-spin" />
                Deleting…
              </>
            ) : first ? (
              'Delete thread'
            ) : (
              'Delete reply'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
